import React, { useState, useMemo, useEffect } from "react";
import { Box, MenuItem, Select, Tooltip } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { AttributeInput } from "../../components/TrackingInputs";
import { seedValueForProperty } from "../../utils/valueType";
import Delete from "../../../icons/Delete";
import Undo from "../../../icons/Undo";
import Info from "../../../icons/Info";
import Warning from "../../../icons/Warning";

import {
  useSpecificationStateStore,
  getItemMetadata,
  type ChangeTarget,
  type Change,
} from "./specificationState";
import {
  getItemAtPath,
  isFieldRequiredByConstraint,
} from "../../utils/conditions";
import {
  getAttributeConstraints,
  useValidationStateStore,
  useViolationSummary,
} from "./validationState";

import {
  Section,
  SectionHeader,
  NoDataText,
  HorizontalPropertyRow,
  PropertyLabel,
  PropertyValue,
  DeleteButton,
  UndoButton,
  AddRow,
  AddButton,
  NewPropertyRow,
} from "../../components/SectionComponents";

type AttributesSectionProps = {
  item: ArenaOnlyItem | Item;
  attributes: Record<string, ValueType>;
  filteredAttributes: [string, ValueType][];
  userDefinedAttributes?: string[];
  sectionRef?: React.RefObject<HTMLDivElement>;
  isParentNew?: boolean;
  readOnly?: boolean;
};

const AttributesSection = ({
  item,
  filteredAttributes,
  userDefinedAttributes = [],
  sectionRef,
  isParentNew = false,
  readOnly = false,
}: AttributesSectionProps) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  // Untouched user-defined rows the user has dismissed for the current item.
  const [dismissedUserDefined, setDismissedUserDefined] = useState<Set<string>>(
    () => new Set()
  );
  const [searchParams] = useSearchParams();

  const { identifier, uuid } = getItemMetadata(item);
  const condition = useSpecificationStateStore((state) => state.condition);
  const formulaDependencies = useValidationStateStore(
    (state) => state.formulaDependencies
  );
  const attributeUuidTranslator = useValidationStateStore(
    (state) => state.attributeUuidTranslator
  );
  const constraints = useValidationStateStore((state) => state.constraints);

  // Subscribe to violations reactively so warning icons update immediately
  const violationSummary = useViolationSummary();
  const trackChange = useSpecificationStateStore((state) => state.trackChange);
  const getChange = useSpecificationStateStore((state) => state.getChange);

  // Get current path from URL params
  const currentPath = searchParams.get("bom-item") || "";

  // Reset the "add attribute" row when navigating to a different BOM item.
  // The component instance is reused across item navigation (constant key),
  // so without this the in-progress add row would persist onto the next item.
  useEffect(() => {
    setIsAddingNew(false);
    setDismissedUserDefined(new Set());
  }, [currentPath]);

  // Get the current item from the BOM tree based on path
  const currentItem = useMemo(() => {
    return getItemAtPath(condition, currentPath);
  }, [condition, currentPath]);

  // Get attributes that have been added via changes
  const addedAttributes = useMemo(() => {
    const added: [string, ValueType][] = [];

    if (!currentItem || !currentItem.item_changes) {
      return added;
    }

    // Check item_changes array for additions
    currentItem.item_changes.forEach((change) => {
      if (change.is_added) {
        // Check if this attribute is not already in the filteredAttributes
        const existsInFiltered = filteredAttributes.some(
          ([key]) => key === change.name
        );
        if (!existsInFiltered) {
          added.push([change.name, change.new_value ?? ""]);
        }
      }
    });
    return added;
  }, [currentItem, filteredAttributes]);

  // Combine existing attributes with newly added ones and sort alphabetically
  const allAttributes = useMemo(() => {
    return [...filteredAttributes, ...addedAttributes].sort((a, b) =>
      a[0].localeCompare(b[0])
    );
  }, [filteredAttributes, addedAttributes]);

  // User-defined attributes with no value yet. Nothing is in the store for
  // these until the user edits the input; the first edit tracks an "add"
  // and writes the value into item.attributes, so the row moves into
  // allAttributes and drops out of this list automatically.
  const pendingUserDefinedAttributes = useMemo(() => {
    const existingNames = new Set(allAttributes.map(([key]) => key));
    const definitions =
      "item_type" in item && item.item_type ? item.item_type.attributes : [];
    return userDefinedAttributes
      .filter(
        (name) => !existingNames.has(name) && !dismissedUserDefined.has(name)
      )
      .map((name): [string, ValueType] => [
        name,
        seedValueForProperty(definitions.find((a) => a.name === name)),
      ]);
  }, [userDefinedAttributes, dismissedUserDefined, allAttributes, item]);

  // Every row to render, sorted alphabetically.
  const displayedAttributes = useMemo(
    () =>
      [...allAttributes, ...pendingUserDefinedAttributes].sort((a, b) =>
        a[0].localeCompare(b[0])
      ),
    [allAttributes, pendingUserDefinedAttributes]
  );

  // Check if an attribute is added
  const isAttributeAdded = (attributeName: string) => {
    // If parent is new, all attributes appear as new
    if (isParentNew) {
      return true;
    }
    // Consult the change record directly. Once an attribute is selected its
    // value is written into item.attributes, so it surfaces in
    // filteredAttributes and gets excluded from addedAttributes. Relying on
    // addedAttributes alone would then miss it, leaving the row unhighlighted.
    const change = getChange({
      type: "attribute",
      path: currentPath,
      propertyName: attributeName,
    });
    if (change?.operation === "add") {
      return true;
    }
    return addedAttributes.some(([key]) => key === attributeName);
  };

  // Check if an attribute is deleted
  const isAttributeDeleted = (attributeName: string) => {
    const target: ChangeTarget = {
      type: "attribute",
      path: currentPath,
      propertyName: attributeName,
    };
    const change = getChange(target);
    return change?.operation === "delete";
  };

  // Get available attributes from item_type.attributes that aren't already in the item or added
  const availableAttributes = useMemo(() => {
    if (!("item_type" in item) || !item.item_type) {
      return [];
    }

    const existingAttributeNames = new Set(
      displayedAttributes.map(([key]) => key)
    );

    return item.item_type.attributes
      .filter((attr) => !existingAttributeNames.has(attr.name))
      .map((attr) => attr.name);
  }, [item, displayedAttributes]);

  const handleAddClick = () => {
    setIsAddingNew(true);
  };

  const handleAttributeSelect = (attributeName: string) => {
    // Immediately track the addition with null value when attribute is selected
    if (attributeName && uuid) {
      const target: ChangeTarget = {
        type: "attribute",
        path: currentPath,
        propertyName: attributeName,
      };
      const change: Change = {
        target,
        operation: "add",
        currentValue: seedValueForAttribute(attributeName),
        previousValue: undefined,
        timestamp: Date.now(),
      };
      trackChange(change);
      // Close the add mode
      setIsAddingNew(false);
    }
  };

  const handleDelete = (attributeName: string) => {
    const target: ChangeTarget = {
      type: "attribute",
      path: currentPath,
      propertyName: attributeName,
    };

    // An untouched user-defined row has nothing in the change model to
    // remove, so just hide it; the attribute returns to the "+" dropdown.
    if (pendingUserDefinedAttributes.some(([key]) => key === attributeName)) {
      setDismissedUserDefined((prev) => new Set(prev).add(attributeName));
      return;
    }

    // If this is a newly added attribute, remove it entirely. removeChange
    // strips both the change record and the pending value from
    // item.attributes, so the row disappears (or, for a user-defined
    // attribute, reverts to an empty row).
    const existingChange = getChange(target);
    if (existingChange && existingChange.operation === "add") {
      // If it was newly added, just remove it from changes completely
      const removeChange = useSpecificationStateStore.getState().removeChange;
      removeChange(target);
    } else {
      // If it's an existing attribute, track as deletion
      const currentAttr = filteredAttributes.find(
        ([key]) => key === attributeName
      );
      const previousValue = currentAttr ? currentAttr[1] : undefined;

      const change: Change = {
        target,
        operation: "delete",
        currentValue: undefined,
        previousValue: previousValue,
        timestamp: Date.now(),
      };
      trackChange(change);
    }
  };

  const handleUndo = (attributeName: string) => {
    const target: ChangeTarget = {
      type: "attribute",
      path: currentPath,
      propertyName: attributeName,
    };

    // Remove the deletion change to restore the attribute
    const removeChange = useSpecificationStateStore.getState().removeChange;
    removeChange(target);
  };

  // Pick a type-appropriate empty seed for a freshly-added attribute so
  // the renderer (`ValueEditor`) — which dispatches by runtime value
  // type — picks the correct editor. See `seedValueForProperty`.
  const seedValueForAttribute = (attributeName: string): ValueType => {
    if (!("item_type" in item) || !item.item_type) return "";
    return seedValueForProperty(
      item.item_type.attributes.find((a) => a.name === attributeName)
    );
  };

  // Get units for an attribute
  const getAttributeUnits = (attributeName: string): string | null => {
    if (!("item_type" in item) || !item.item_type) {
      return null;
    }
    const attribute = item.item_type.attributes.find(
      (attr) => attr.name === attributeName
    );
    if (
      attribute &&
      attribute.units &&
      attribute.units.toLowerCase() !== "each"
    ) {
      return attribute.units;
    }
    return null;
  };

  // Check if an attribute is computed from a formula
  const getAttributeFormula = (attributeName: string): string | null => {
    // Check all formula dependencies to see if this attribute is a target
    for (const dependencies of Object.values(formulaDependencies)) {
      for (const dep of dependencies) {
        if (
          dep.targetItemUuid === (uuid || identifier) &&
          dep.targetAttributeName === attributeName
        ) {
          // Replace variable aliases with actual attribute names
          let displayFormula = dep.formula;
          dep.variables.forEach((variable) => {
            const sourceLocations = attributeUuidTranslator.get(
              variable.attribute_uuid
            );
            if (sourceLocations && sourceLocations.length > 0) {
              const regex = new RegExp(`\\b${variable.alias}\\b`, "g");
              displayFormula = displayFormula.replace(
                regex,
                sourceLocations[0].attributeName
              );
            }
          });
          return displayFormula;
        }
      }
    }
    return null;
  };

  // Format attribute name with units if applicable
  const formatAttributeName = (attributeName: string): React.ReactNode => {
    const units = getAttributeUnits(attributeName);
    const formula = getAttributeFormula(attributeName);
    const label = units ? `${attributeName} (${units})` : attributeName;

    if (formula) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {label}
          <Tooltip title={`Computed field: ${formula}`} placement="right">
            <Box sx={{ display: "flex", alignItems: "center", cursor: "help" }}>
              <Info style={{ width: "16px", height: "16px", fill: "#666" }} />
            </Box>
          </Tooltip>
        </Box>
      );
    }

    return label;
  };

  // Check if an attribute is required, either by the item type
  // (`is_required`) or by a template-level `required_attribute` constraint.
  const isAttributeRequired = (attributeName: string): boolean => {
    if (!("item_type" in item) || !item.item_type) return false;
    const attr = item.item_type.attributes.find(
      (a) => a.name === attributeName
    );
    if (attr?.is_required) return true;
    if (!uuid) return false;
    return isFieldRequiredByConstraint(
      getAttributeConstraints(constraints, uuid, attributeName)
    );
  };

  // Check if an attribute has any violations using path-based lookup
  const hasAttributeRangeViolation = (attributeName: string): boolean => {
    return violationSummary
      .getAttributeViolationsByPath(currentPath, attributeName)
      .some((violation) => violation.constraint_type === "attribute_range");
  };

  return (
    <Section ref={sectionRef}>
      <SectionHeader>Attributes</SectionHeader>
      {displayedAttributes.length > 0 ? (
        <>
          {displayedAttributes.map(([key, value]) => {
            const isDeleted = isAttributeDeleted(key);
            const isAdded = isAttributeAdded(key);
            return (
              <HorizontalPropertyRow
                key={key}
                isDeleted={isDeleted}
                isAdded={isAdded}
              >
                {hasAttributeRangeViolation(key) && (
                  <Warning
                    style={{
                      fontSize: "16px",
                      color: "#ff9800",
                      marginRight: "8px",
                    }}
                  />
                )}
                <PropertyLabel isDeleted={isDeleted}>
                  {formatAttributeName(key)}
                </PropertyLabel>
                <PropertyValue>
                  <AttributeInput
                    item={item}
                    attributeName={key}
                    attributeValue={{ value }}
                    disabled={isDeleted || readOnly}
                    units={getAttributeUnits(key)}
                  />
                  {isDeleted ? (
                    <UndoButton
                      size="small"
                      onClick={() => handleUndo(key)}
                      aria-label={`Undo delete ${key}`}
                      disabled={readOnly}
                    >
                      <Undo />
                    </UndoButton>
                  ) : (
                    <Tooltip
                      title={
                        isAttributeRequired(key)
                          ? "This attribute is required"
                          : ""
                      }
                      disableHoverListener={!isAttributeRequired(key)}
                    >
                      <span>
                        <DeleteButton
                          size="small"
                          onClick={() => handleDelete(key)}
                          aria-label={`Delete ${key}`}
                          disabled={readOnly || isAttributeRequired(key)}
                        >
                          <Delete />
                        </DeleteButton>
                      </span>
                    </Tooltip>
                  )}
                </PropertyValue>
              </HorizontalPropertyRow>
            );
          })}
        </>
      ) : !isAddingNew || availableAttributes.length === 0 ? (
        <NoDataText>No attributes defined</NoDataText>
      ) : null}

      {isAddingNew && availableAttributes.length > 0 ? (
        <NewPropertyRow>
          <Box sx={{ minWidth: "220px", paddingLeft: 1, paddingRight: 1 }}>
            <Select
              size="small"
              value=""
              onChange={(e) => handleAttributeSelect(e.target.value)}
              displayEmpty
              fullWidth
              autoFocus
              disabled={readOnly}
              sx={{
                fontSize: "14px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d0d0d0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#b0b0b0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#1976d2",
                },
              }}
            >
              <MenuItem value="" disabled>
                <em>Select attribute...</em>
              </MenuItem>
              {availableAttributes.map((attrName) => (
                <MenuItem key={attrName} value={attrName}>
                  {attrName}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </NewPropertyRow>
      ) : availableAttributes.length > 0 ? (
        <AddRow
          onClick={readOnly ? undefined : handleAddClick}
          disabled={readOnly}
        >
          <AddButton disabled={readOnly}>+</AddButton>
        </AddRow>
      ) : null}
    </Section>
  );
};

export default AttributesSection;
